import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { createApiUrlWithPrefix } from '../config/api';
import { useSettings } from '../contexts/SettingsContext';
import { 
  Plus,
  Search,
  CheckCircle,
  XCircle,
  User,
  CreditCard,
  Receipt,
  Eye,
  Trash2,
  Coins,
  Calendar,
  DollarSign,
  RefreshCw,
  X
} from 'lucide-react';

const TemporaryUserPayments = () => {
  const { t, i18n } = useTranslation();
  const { getSettingAsNumber } = useSettings();
  const jetonFallbackPrice = getSettingAsNumber('jeton.base.price', 2000);
  const [temporaryUsers, setTemporaryUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedTemporaryUser, setSelectedTemporaryUser] = useState(null);
  const [temporaryUserPayments, setTemporaryUserPayments] = useState([]);
  const [showPaymentHistory, setShowPaymentHistory] = useState(false);
  const [selectedPaymentType, setSelectedPaymentType] = useState('all');
  const [userJetons, setUserJetons] = useState({}); // Store jeton data for each user
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(12);
  const [totalPages, setTotalPages] = useState(1);

  // Payment form state
  const [paymentForm, setPaymentForm] = useState({
    amount: '',
    jetonPackage: 1, // Selected jeton package
    paymentMethod: 0, // Cash
    description: '',
    notes: ''
  });

  const [refreshKey, setRefreshKey] = useState(0);
  
  // Jeton packages fetched from server configuration
  const [jetonPackages, setJetonPackages] = useState([]);
  const [baseJetonPrice, setBaseJetonPrice] = useState(jetonFallbackPrice);

  useEffect(() => {
    fetchJetonPackages();
  }, []);

  const fetchJetonPackages = async () => {
    try {
      const response = await axios.get(createApiUrlWithPrefix('settings/jeton-price'));
      const data = response.data;
      setBaseJetonPrice(data.basePrice);
      const packages = data.packages.map((pkg, index) => ({
        value: pkg.quantity,
        label: `${pkg.quantity} Jeton${pkg.quantity > 1 ? 's' : ''}`,
        icon: Coins,
        color: [
          'bg-blue-100 text-blue-800',
          'bg-green-100 text-green-800',
          'bg-orange-100 text-orange-800',
          'bg-purple-100 text-purple-800',
          'bg-indigo-100 text-indigo-800',
          'bg-pink-100 text-pink-800',
          'bg-yellow-100 text-yellow-800',
          'bg-red-100 text-red-800'
        ][index] || 'bg-blue-100 text-blue-800',
        description: pkg.quantity === 1 
          ? t('common.singleJetonPurchase') 
          : `${pkg.quantity} jetons with ${pkg.discountPercent}% discount`,
        pricePerJeton: pkg.pricePerJeton,
        quantity: pkg.quantity,
        totalAmount: pkg.totalAmount,
        discount: pkg.discountPercent,
        enabled: true
      }));
      setJetonPackages(packages);
    } catch (error) {
      console.error('Error fetching jeton packages:', error);
    }
  };

  const paymentMethods = useMemo(() => {
    return [
      { 
        value: 0, 
        label: t('common.cash'), 
        icon: DollarSign, 
        color: 'bg-green-100 text-green-800',
        description: t('common.cashPayment')
      },
      { 
        value: 1, 
        label: t('common.bankTransfer'), 
        icon: CreditCard, 
        color: 'bg-blue-100 text-blue-800',
        description: t('common.bankTransferPayment')
      },
      {
        value: 2, 
        label: t('common.creditCard'), 
        icon: CreditCard, 
        color: 'bg-purple-100 text-purple-800',
        description: t('common.creditCardPayment')
      },
      {
        value: 3, 
        label: t('common.other'), 
        icon: Receipt, 
        color: 'bg-gray-100 text-gray-800',
        description: t('common.otherPaymentMethod')
      }
    ];
  }, []);

  // Set initial payment form values when jeton package changes
  useEffect(() => {
    const selectedPackage = jetonPackages.find(pkg => pkg.value === paymentForm.jetonPackage);
    if (selectedPackage) {
      setPaymentForm(prev => ({
        ...prev,
        amount: selectedPackage.totalAmount.toString(),
        description: selectedPackage.label
      }));
    }
  }, [paymentForm.jetonPackage, jetonPackages]);

  // Fetch jeton data for a specific user
  const fetchUserJetons = useCallback(async (userId) => {
    try {
      const response = await axios.get(createApiUrlWithPrefix(`jeton/user/${userId}`), {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      return response.data || [];
    } catch (error) {
      console.error(`Failed to fetch jetons for user ${userId}:`, error);
      return [];
    }
  }, []);

  // Fetch jeton data for all users
  const fetchAllUserJetons = useCallback(async (userList) => {
    const jetonData = {};
    for (const user of userList) {
      const jetons = await fetchUserJetons(user.id);
      jetonData[user.id] = jetons;
    }
    setUserJetons(jetonData);
  }, [fetchUserJetons]);

  const fetchTemporaryUsers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(createApiUrlWithPrefix('users?role=5&limit=100'), {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      const userList = response.data.users || response.data || [];
      setTemporaryUsers(userList);
      
      // Fetch jeton data for all users
      if (userList.length > 0) {
        await fetchAllUserJetons(userList);
      }
    } catch (error) {
      console.error('Failed to fetch temporary users:', error);
      toast.error(t('common.failedToLoadTemporaryUsers'));
    } finally {
      setLoading(false);
    }
  }, [fetchAllUserJetons]);

  const fetchTemporaryUserPayments = useCallback(async (userId) => {
    try {
      const response = await axios.get(createApiUrlWithPrefix(`temporaryuserpayment/user/${userId}`), {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      setTemporaryUserPayments(response.data || []);
    } catch (error) {
      console.error('Failed to fetch payments:', error);
      toast.error('Failed to load payment history');
    }
  }, []);

  const handleAddPayment = (user) => {
    setSelectedTemporaryUser(user);
    const initialPackage = jetonPackages.find(pkg => pkg.value === 1);
    setPaymentForm({
      amount: initialPackage.totalAmount.toString(),
      jetonPackage: 1,
      paymentMethod: 0,
      description: initialPackage.label,
      notes: ''
    });
    setShowPaymentModal(true);
  };

  const handleViewPayments = (user) => {
    setSelectedTemporaryUser(user);
    fetchTemporaryUserPayments(user.id);
    setShowPaymentHistory(true);
  };

  const handleSubmitPayment = async () => {
    if (!selectedTemporaryUser) return;

    try {
      const selectedPackage = jetonPackages.find(pkg => pkg.value === paymentForm.jetonPackage);
      if (!selectedPackage) {
        toast.error('Please select a valid jeton package');
        return;
      }

      // Create the payment (service will create jeton automatically)
      const paymentData = {
        temporaryUserId: selectedTemporaryUser.id,
        jetonQuantity: selectedPackage.quantity,
        amount: parseFloat(paymentForm.amount),
        pricePerJeton: selectedPackage.pricePerJeton,
        paymentMethod: paymentForm.paymentMethod,
        notes: paymentForm.notes
      };

      await axios.post(createApiUrlWithPrefix('temporaryuserpayment'), paymentData, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      toast.success(`${selectedPackage.label} payment added successfully!`);
      setShowPaymentModal(false);
      setSelectedTemporaryUser(null);
      
      // Refresh the temporary users list and jeton data
      fetchTemporaryUsers();
    } catch (error) {
      console.error('Failed to add payment:', error);
      console.error('Error details:', error.response?.data);
      
      let errorMessage = 'Failed to add payment';
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
    }
  };

  const handleDeletePayment = async (paymentId) => {
    if (!window.confirm('Are you sure you want to delete this payment? This will also remove the associated jetons.')) return;

    try {
      await axios.delete(createApiUrlWithPrefix(`temporaryuserpayment/${paymentId}`), {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      toast.success('Payment and associated jetons deleted successfully!');
      
      // Refresh payment history
      if (selectedTemporaryUser) {
        fetchTemporaryUserPayments(selectedTemporaryUser.id);
        // Refresh jeton data for the user
        const updatedJetons = await fetchUserJetons(selectedTemporaryUser.id);
        setUserJetons(prev => ({
          ...prev,
          [selectedTemporaryUser.id]: updatedJetons
        }));
      }
      
      // Refresh the main users list to update jeton counts
      fetchTemporaryUsers();
    } catch (error) {
      console.error('Failed to delete payment:', error);
      toast.error('Failed to delete payment');
    }
  };

  const filteredUsers = useMemo(() => {
    if (!searchTerm) return temporaryUsers;
    return temporaryUsers.filter(user => 
      user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [temporaryUsers, searchTerm]);

  const paginatedUsers = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredUsers.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredUsers, currentPage, itemsPerPage]);

  useEffect(() => {
    fetchTemporaryUsers();
  }, [fetchTemporaryUsers]);

  const getStatusBadge = (status) => {
    switch (status) {
      case 0:
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
          <CheckCircle className="w-3 h-3 mr-1" />
          Active
        </span>;
      case 1:
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
          <XCircle className="w-3 h-3 mr-1" />
          Deactivated
        </span>;
      default:
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
          Unknown
        </span>;
    }
  };

  const getPaymentMethodBadge = (method) => {
    const methodData = paymentMethods.find(m => m.value === method);
    if (!methodData) return null;
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${methodData.color}`}>
        {methodData.label}
      </span>
    );
  };


  // Calculate jeton statistics for a user
  const getJetonStats = (userId) => {
    const jetons = userJetons[userId] || [];
    const totalJetons = jetons.reduce((sum, jeton) => sum + jeton.quantity, 0);
    const remainingJetons = jetons.reduce((sum, jeton) => sum + jeton.remainingQuantity, 0);
    const usedJetons = totalJetons - remainingJetons;
    const totalSpent = jetons.reduce((sum, jeton) => sum + jeton.totalAmount, 0);
    
    return {
      total: totalJetons,
      remaining: remainingJetons,
      used: usedJetons,
      totalSpent: totalSpent
    };
  };

  /* removed early loading return to prevent search bar unmounting 
if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading temporary users...</p>
        </div>
      </div>
    );
  } 
*/

  return (
    <>
      <div className="flex flex-col min-h-full bg-neutral-50 px-4 sm:px-6 lg:px-8 py-6" dir={i18n.language === 'ar' ? 'rtl' : 'ltr'} key={refreshKey}>
        {/* Fixed Header Section */}
        <div className="">
          {/* Header - Responsive */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">{t('common.temporaryUserPayments')}</h1>
              <p className="mt-2 sm:mt-3 text-sm sm:text-base text-gray-600">{t('common.managePaymentsAndJetons')}</p>
            </div>
          </div>

          {/* Search Bar */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mt-4">
            <div className="flex flex-col lg:flex-row gap-4 items-center">
              {/* Search Input */}
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="text"
                    placeholder={t('users.searchPlaceholder')}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-4 text-lg border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
                  />
                </div>
              </div>
              
              {/* Action Buttons */}
              <div className="flex items-center space-x-3">
                <button
                  onClick={fetchTemporaryUsers}
                  className="px-6 py-3 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2"
                >
                  <RefreshCw className="w-4 h-4" />
                  Refresh
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Scrollable Content Section */}
        <div className="mt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {paginatedUsers.map((user) => (
          <div key={user.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                  <User className="w-6 h-6 text-orange-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">
                    {user.name || `${user.firstName} ${user.lastName}`}
                  </h3>
                  <p className="text-sm text-gray-500">{user.email}</p>
                </div>
              </div>
            </div>
            
            {/* Status Cards */}
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div className="bg-orange-50 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-1">
                  <Coins className="w-4 h-4 text-orange-600" />
                  <span className="text-xs font-medium text-orange-800">{t('common.jetons')}</span>
                </div>
                <div className="flex items-center gap-1 mb-1">
                  <span className="text-xs text-gray-600">{t('common.available')}</span>
                </div>
                <p className="text-xs font-semibold text-orange-700">
{getJetonStats(user.id).remaining} {t('common.jetons')}
                </p>
                {getJetonStats(user.id).used > 0 && (
                  <p className="text-xs text-gray-500">
{t('common.used')}: {getJetonStats(user.id).used}
                  </p>
                )}
              </div>

              <div className="bg-blue-50 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-1">
                  <CreditCard className="w-4 h-4 text-blue-600" />
                  <span className="text-xs font-medium text-blue-800">{t('common.payments')}</span>
                </div>
                <div className="flex items-center gap-1">
                  <span className="text-xs text-gray-600">{t('common.total')}</span>
                </div>
                <p className="text-xs text-gray-500">
                  {getJetonStats(user.id).totalSpent.toLocaleString()} DA
                </p>
              </div>
            </div>
            
            {/* Action Buttons */}
            <div className="flex gap-2">
              <button
                onClick={() => handleAddPayment(user)}
                className="flex-1 bg-blue-600 text-white px-3 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 flex items-center justify-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Add Payment
              </button>
              <button
                onClick={() => handleViewPayments(user)}
                className="flex-1 bg-gray-100 text-gray-700 px-3 py-2 rounded-lg text-sm font-medium hover:bg-gray-200 flex items-center justify-center gap-2"
              >
                <Eye className="w-4 h-4" />
                History
              </button>
            </div>
          </div>
        ))}
      )}

      {/* Empty State */}
      {filteredUsers.length === 0 && (
        <div className="text-center py-12">
          <User className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">{t('common.noTemporaryUsersFound')}</h3>
          <p className="text-gray-500">
            {searchTerm ? 'Try adjusting your search criteria' : 'No temporary users have been created yet'}
          </p>
        </div>
      )}
          </div>
        </div>

        {/* Pagination - Sticky at bottom */}
        <div className="mt-6 bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 sm:gap-4">
            <div className="text-xs sm:text-sm text-gray-700 font-medium">
              Showing <span className="font-bold text-blue-600">{((currentPage - 1) * itemsPerPage) + 1}</span> to <span className="font-bold text-blue-600">{Math.min(currentPage * itemsPerPage, filteredUsers.length)}</span> of <span className="font-bold text-blue-600">{filteredUsers.length}</span> results
            </div>
            
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1 || totalPages <= 1}
                className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px] min-w-[44px] touch-manipulation"
              >
                Previous
              </button>
              
              <span className="px-3 py-2 text-sm text-gray-700">
                Page {currentPage} of {totalPages}
              </span>
              
              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages || totalPages <= 1}
                className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px] min-w-[44px] touch-manipulation"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Add Payment Modal */}
      {showPaymentModal && selectedTemporaryUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-1">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-xs sm:max-w-sm md:max-w-md max-h-[96vh] overflow-hidden flex flex-col mx-1">
            <div className="p-2 sm:p-3 flex-shrink-0">
              <h2 className="text-sm sm:text-base font-semibold text-gray-900 mb-2">
                Add Payment - {selectedTemporaryUser.name || `${selectedTemporaryUser.firstName} ${selectedTemporaryUser.lastName}`}
              </h2>
              
              <div className="overflow-y-auto flex-1 pr-2">

                <form id="temporary-payment-form" onSubmit={(e) => { e.preventDefault(); handleSubmitPayment(); }} className="space-y-2">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
{t('common.jetonPackage')}
                    </label>
                    <select
                      value={paymentForm.jetonPackage}
                      onChange={(e) => {
                        const selectedQty = parseInt(e.target.value);
                        const selectedPkg = jetonPackages.find(p => p.value === selectedQty);
                        setPaymentForm(prev => ({
                          ...prev,
                          jetonPackage: selectedQty,
                          amount: selectedPkg ? selectedPkg.totalAmount.toString() : prev.amount
                        }));
                      }}
                      className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      {jetonPackages.map((pkg) => (
                        <option key={pkg.value} value={pkg.value}>
                          {pkg.label} - {pkg.totalAmount.toLocaleString()} DA {pkg.discount > 0 && `(${pkg.discount}% off)`}
                        </option>
                      ))}
                    </select>
                    
                    {/* Jeton Packages Info */}
                    <div className="mt-1 space-y-0.5">
                      {jetonPackages.slice(0, 4).map(pkg => (
                        <div key={pkg.value} className="flex items-center gap-1 text-xs">
                          <div className="w-1.5 h-1.5 rounded-full bg-green-500"></div>
                          <span className="text-gray-700">
                            {pkg.label}: {pkg.totalAmount.toLocaleString()} DA
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
{t('common.totalAmount')} (DA)
                    </label>
                    <input
                      type="number"
                      value={paymentForm.amount}
                      readOnly
                      className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg bg-gray-50"
                    />
                    {(() => {
                      const selectedPackage = jetonPackages.find(pkg => pkg.value === paymentForm.jetonPackage);
                      if (selectedPackage && selectedPackage.discount > 0) {
                        const savings = (baseJetonPrice - selectedPackage.pricePerJeton) * selectedPackage.quantity;
                        return (
                          <p className="text-xs text-green-600 mt-1">
                            💡 You save {savings.toLocaleString()} DA compared to individual purchases!
                          </p>
                        );
                      }
                      return null;
                    })()}
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
{t('common.paymentMethod')}
                    </label>
                    <select
                      value={paymentForm.paymentMethod}
                      onChange={(e) => setPaymentForm(prev => ({ ...prev, paymentMethod: parseInt(e.target.value) }))}
                      className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      {paymentMethods.map((method) => (
                        <option key={method.value} value={method.value}>
                          {method.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
{t('common.notes')}
                    </label>
                    <textarea
                      value={paymentForm.notes}
                      onChange={(e) => setPaymentForm(prev => ({ ...prev, notes: e.target.value }))}
                      rows={2}
                      className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder={t('payments.additionalNotes')}
                    />
                  </div>
                </form>
              </div>
              
              {/* Fixed Footer with Buttons */}
              <div className="p-2 border-t border-gray-200 bg-gray-50 flex-shrink-0">
                <div className="flex flex-col sm:flex-row gap-1.5">
                  <button
                    type="submit"
                    form="temporary-payment-form"
                    className="flex-1 bg-blue-600 text-white py-1.5 px-3 rounded-lg text-sm font-medium hover:bg-blue-700"
                  >
{t('common.createPayment')}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowPaymentModal(false)}
                    className="flex-1 bg-gray-100 text-gray-700 py-1.5 px-3 rounded-lg text-sm font-medium hover:bg-gray-200"
                  >
{t('common.cancel')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Payment History Modal */}
      {showPaymentHistory && selectedTemporaryUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[80vh] overflow-hidden">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-900">
                  Payment History - {selectedTemporaryUser.name || `${selectedTemporaryUser.firstName} ${selectedTemporaryUser.lastName}`}
                </h2>
                <button
                  onClick={() => setShowPaymentHistory(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>
            
            <div className="p-6 overflow-y-auto max-h-[60vh]">

              {temporaryUserPayments.length === 0 ? (
                <div className="text-center py-8">
                  <Receipt className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500">{t('common.noPaymentsFound')}</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {temporaryUserPayments.map((payment) => {
                    const paymentMethod = paymentMethods.find(m => m.value === payment.paymentMethod);
                    const IconComponent = paymentMethod?.icon || CreditCard;
                    
                    return (
                      <div key={payment.id} className="bg-gray-50 rounded-lg p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className={`p-2 rounded-lg ${paymentMethod?.color || 'bg-gray-100'}`}>
                              <IconComponent className="w-5 h-5" />
                            </div>
                            <div>
                              <h3 className="font-medium text-gray-900">
                                {payment.numberOfJetonsPurchased} Jetons - {payment.amount.toLocaleString()} DA
                              </h3>
                              <p className="text-sm text-gray-500">{payment.notes || 'Jeton purchase'}</p>
                              <p className="text-xs text-gray-400">
                                {new Date(payment.paymentDate).toLocaleDateString()} • {paymentMethod?.label || 'Unknown'}
                              </p>
                              <div className="flex items-center gap-2 mt-1">
                                <Coins className="w-3 h-3 text-orange-500" />
                                <span className="text-xs text-orange-600">
{payment.numberOfJetonsPurchased} {t('common.jetons')} @ {payment.pricePerJeton.toLocaleString()} DA {t('common.each')}
                                </span>
                              </div>
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-3">
                            <div className="text-right">
                              <p className="font-semibold text-gray-900">{payment.amount.toLocaleString()} DA</p>
                            </div>
                            
                            <button
                              onClick={() => handleDeletePayment(payment.id)}
                              className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                              title={t('payments.deletePayment')}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default TemporaryUserPayments;